import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Minus, Shield, Clock, Target,
  CheckCircle, XCircle, Loader2, ChevronDown, BarChart3,
  AlertTriangle, Filter
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_WINDOWS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
  { label: 'All Time', value: 9999 },
];

const outcomeColors = {
  correct: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: CheckCircle },
  incorrect: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: XCircle },
  neutral: { text: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30', icon: Minus },
  pending: { text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: Clock },
};

const impactBadge = {
  bullish: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: TrendingUp },
  bearish: { text: 'text-red-400', bg: 'bg-red-500/10', icon: TrendingDown },
};

const confBadge = {
  high: { text: 'text-orange-400', bg: 'bg-orange-500/10' },
  medium: { text: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  low: { text: 'text-gray-400', bg: 'bg-gray-500/10' },
};

function getTimeAgo(isoString) {
  if (!isoString) return '';
  const diff = (Date.now() - new Date(isoString).getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatPrice(p) {
  if (!p) return '—';
  return p >= 1 ? `$${p.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : `$${p.toFixed(4)}`;
}

const AccuracyRing = ({ value, label, size = 120 }) => {
  const pct = value ?? 0;
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  let color = '#6b7280';
  if (pct >= 60) color = '#10b981';
  else if (pct >= 40) color = '#eab308';
  else if (pct > 0) color = '#ef4444';

  return (
    <div className="flex flex-col items-center" data-testid={`accuracy-ring-${label}`}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#1a1a1a" strokeWidth="8" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={circumference} strokeDashoffset={value !== null ? offset : circumference}
          strokeLinecap="round" className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-2xl font-bold text-white font-mono">
          {value !== null ? `${Math.round(value)}%` : '—'}
        </span>
      </div>
      <span className="text-xs text-gray-500 mt-2 uppercase tracking-wider">{label}</span>
    </div>
  );
};

const OutcomeBadge = ({ outcome }) => {
  const cfg = outcomeColors[outcome] || outcomeColors.pending;
  const Icon = cfg.icon;
  const labels = { correct: 'Correct', incorrect: 'Incorrect', neutral: 'Neutral', pending: 'Pending' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${cfg.text} ${cfg.bg} border ${cfg.border}`}>
      <Icon size={11} />
      {labels[outcome] || 'Pending'}
    </span>
  );
};

const TrackRecord = () => {
  const [days, setDays] = useState(90);
  const [stats, setStats] = useState(null);
  const [calls, setCalls] = useState([]);
  const [callsTotal, setCallsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 15;

  const fetchData = useCallback(async (timeWindow) => {
    setLoading(true);
    setOffset(0);
    try {
      const [statsRes, callsRes] = await Promise.all([
        axios.get(`${API}/track-record/stats?days=${timeWindow}`),
        axios.get(`${API}/track-record/calls?days=${timeWindow}&limit=${LIMIT}&offset=0`),
      ]);
      setStats(statsRes.data);
      setCalls(callsRes.data.items || []);
      setCallsTotal(callsRes.data.total || 0);
    } catch (err) {
      console.error('Track record fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(days);
  }, [days, fetchData]);

  const loadMore = async () => {
    const newOffset = offset + LIMIT;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/track-record/calls?days=${days}&limit=${LIMIT}&offset=${newOffset}`);
      setCalls(prev => [...prev, ...(res.data.items || [])]);
      setOffset(newOffset);
    } catch (err) {
      console.error('Load more error:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const o = stats?.overall || {};
  const bc = stats?.by_confidence || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="track-record-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Target size={16} className="text-[#C5A059]" />
            <span className="text-xs text-[#C5A059] font-semibold uppercase tracking-wider">Transparency Report</span>
          </div>
          <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-2">
            AI Track <span className="text-[#C5A059]">Record</span>
          </h1>
          <p className="text-gray-400 text-sm max-w-2xl">
            Every call our AI makes is tracked against real price movement. No cherry-picking, no hiding losses.
            This is how we prove our intelligence engine works.
          </p>
        </div>

        {/* Time Window Filter */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="time-filter">
          {TIME_WINDOWS.map((tw) => (
            <button
              key={tw.value}
              onClick={() => setDays(tw.value)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all whitespace-nowrap ${
                days === tw.value
                  ? 'bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/40'
                  : 'bg-white/[0.03] text-gray-500 border border-transparent hover:border-white/10 hover:text-gray-300'
              }`}
              data-testid={`filter-${tw.value}d`}
            >
              {tw.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-[#C5A059] animate-spin" />
          </div>
        ) : (
          <>
            {/* Hero Accuracy Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {['24h', '48h', '7d'].map((window) => (
                <div key={window} className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-white/10 rounded-xl p-6 text-center relative">
                  <div className="relative inline-block">
                    <AccuracyRing value={o[`accuracy_${window}`]} label={`${window} Accuracy`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Total calls badge */}
            <div className="flex items-center justify-center gap-4 mb-8 flex-wrap">
              <div className="bg-white/5 border border-white/10 rounded-full px-5 py-2 flex items-center gap-2">
                <BarChart3 size={14} className="text-[#C5A059]" />
                <span className="text-sm text-gray-300"><strong className="text-white">{o.total || 0}</strong> total calls</span>
              </div>
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-full px-5 py-2 flex items-center gap-2">
                <TrendingUp size={14} className="text-emerald-400" />
                <span className="text-sm text-gray-300"><strong className="text-emerald-400">{o.bullish_count || 0}</strong> bullish</span>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-full px-5 py-2 flex items-center gap-2">
                <TrendingDown size={14} className="text-red-400" />
                <span className="text-sm text-gray-300"><strong className="text-red-400">{o.bearish_count || 0}</strong> bearish</span>
              </div>
              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-full px-5 py-2 flex items-center gap-2">
                <Clock size={14} className="text-yellow-400" />
                <span className="text-sm text-gray-300"><strong className="text-yellow-400">{o.pending || 0}</strong> pending</span>
              </div>
            </div>

            {/* Engine Comparison */}
            {stats?.engines && (
              <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-[#C5A059]/25 rounded-xl overflow-hidden mb-8" data-testid="engine-comparison">
                <div className="px-6 py-4 border-b border-white/10">
                  <h2 className="font-manrope font-bold text-lg text-white flex items-center gap-2">
                    <BarChart3 size={16} className="text-[#C5A059]" />
                    Engine Upgrade: Consensus v2
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Our engine was upgraded to a 3-model consensus system with conviction gating — calls are only published
                    when at least 2 independent AI models agree at 70%+ conviction. Both engines shown for full transparency.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
                  <div className="p-5 text-center">
                    <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-1">Legacy Engine (single model)</p>
                    <p className="font-manrope font-extrabold text-2xl text-gray-400">
                      {stats.engines.legacy.accuracy_24h != null ? `${stats.engines.legacy.accuracy_24h}%` : '—'}
                    </p>
                    <p className="text-gray-600 text-xs mt-1">{stats.engines.legacy.total} calls · retired</p>
                  </div>
                  <div className="p-5 text-center bg-[#C5A059]/5">
                    <p className="text-[#C5A059] text-[10px] font-bold uppercase tracking-widest mb-1">Consensus Engine v2 · Live</p>
                    <p className="font-manrope font-extrabold text-2xl text-[#C5A059]">
                      {stats.engines.consensus.total >= 10 && stats.engines.consensus.accuracy_24h != null
                        ? `${stats.engines.consensus.accuracy_24h}%` : '—'}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {stats.engines.consensus.total >= 10 && stats.engines.consensus.accuracy_24h != null
                        ? `${stats.engines.consensus.total} calls`
                        : `building verified sample (${stats.engines.consensus.total} call${stats.engines.consensus.total === 1 ? '' : 's'} so far)`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Macro alignment breakdown */}
            {stats?.by_macro && (stats.by_macro.aligned.total > 0 || stats.by_macro.against.total > 0) && (
              <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden mb-8" data-testid="macro-alignment-card">
                <div className="px-6 py-4 border-b border-white/10">
                  <h2 className="font-manrope font-bold text-lg text-white">Macro Alignment Analysis</h2>
                  <p className="text-xs text-gray-500 mt-1">How calls perform when the Macro Pulse backdrop agrees vs disagrees with the call direction (tagged since Macro Pulse launch).</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
                  <div className="p-5 text-center">
                    <p className="text-emerald-400 text-[10px] font-bold uppercase tracking-widest mb-1">Macro-Aligned Calls</p>
                    <p className="font-manrope font-extrabold text-2xl text-white">
                      {stats.by_macro.aligned.accuracy_24h != null ? `${stats.by_macro.aligned.accuracy_24h}%` : '—'}
                    </p>
                    <p className="text-gray-600 text-xs mt-1">{stats.by_macro.aligned.total} calls</p>
                  </div>
                  <div className="p-5 text-center">
                    <p className="text-red-400 text-[10px] font-bold uppercase tracking-widest mb-1">Against The Macro</p>
                    <p className="font-manrope font-extrabold text-2xl text-white">
                      {stats.by_macro.against.accuracy_24h != null ? `${stats.by_macro.against.accuracy_24h}%` : '—'}
                    </p>
                    <p className="text-gray-600 text-xs mt-1">{stats.by_macro.against.total} calls</p>
                  </div>
                </div>
              </div>
            )}

            {/* Confidence Breakdown Table */}
            <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden mb-8" data-testid="confidence-table">
              <div className="px-6 py-4 border-b border-white/10">
                <h2 className="font-manrope font-bold text-lg text-white flex items-center gap-2">
                  <Shield size={16} className="text-[#C5A059]" />
                  Accuracy by Confidence Level
                </h2>
                <p className="text-xs text-gray-500 mt-1">Does "High Confidence" actually mean something? This table proves it.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">Confidence</th>
                      <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Calls</th>
                      <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">24h Accuracy</th>
                      <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">48h Accuracy</th>
                      <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">7d Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['high', 'medium', 'low'].map((bucket) => {
                      const d = bc[bucket] || {};
                      const cb = confBadge[bucket];
                      return (
                        <tr key={bucket} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase ${cb.text} ${cb.bg}`}>
                              <Shield size={11} />
                              {bucket}
                            </span>
                          </td>
                          <td className="text-center px-4 py-4 text-gray-300 font-mono">{d.total || 0}</td>
                          {['24h', '48h', '7d'].map((w) => {
                            const val = d[`accuracy_${w}`];
                            let color = 'text-gray-600';
                            if (val !== null && val !== undefined) {
                              if (val >= 60) color = 'text-emerald-400';
                              else if (val >= 40) color = 'text-yellow-400';
                              else color = 'text-red-400';
                            }
                            return (
                              <td key={w} className={`text-center px-4 py-4 font-bold font-mono ${color}`}>
                                {val !== null && val !== undefined ? `${Math.round(val)}%` : '—'}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Individual Calls List */}
            <div className="mb-4">
              <h2 className="font-manrope font-bold text-lg text-white flex items-center gap-2">
                <BarChart3 size={16} className="text-[#C5A059]" />
                All Calls
                <span className="text-sm text-gray-500 font-normal">({callsTotal})</span>
              </h2>
              <p className="text-xs text-gray-500 mt-1">Every call shown — correct and incorrect. Full transparency.</p>
            </div>

            {calls.length === 0 ? (
              <div className="text-center py-16 bg-white/[0.02] rounded-xl border border-white/5">
                <Target size={36} className="text-gray-700 mx-auto mb-3" />
                <h3 className="text-gray-400 font-bold mb-1">No calls tracked yet</h3>
                <p className="text-gray-600 text-sm">
                  The AI intelligence engine is running. Calls will appear here once generated.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {calls.map((call) => {
                  const imp = impactBadge[call.impact_call] || {};
                  const ImpIcon = imp.icon || Minus;
                  const cb = confBadge[call.confidence_bucket] || confBadge.medium;
                  const bestOutcome = call.outcome_7d !== 'pending' ? call.outcome_7d
                    : call.outcome_48h !== 'pending' ? call.outcome_48h
                    : call.outcome_24h !== 'pending' ? call.outcome_24h
                    : 'pending';

                  return (
                    <div
                      key={call.call_id}
                      className="bg-gradient-to-r from-[#111111] to-[#0A0A0A] border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors"
                      data-testid={`call-${call.call_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {/* Left: Headline + Meta */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-sm mb-1.5 line-clamp-1">{call.headline}</h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Asset badge */}
                            <span className="text-[10px] font-bold bg-white/5 text-gray-300 px-2 py-0.5 rounded uppercase">
                              {call.asset_ticker}
                            </span>
                            {/* Impact badge */}
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${imp.text} ${imp.bg}`}>
                              <ImpIcon size={10} />
                              {call.impact_call}
                            </span>
                            {/* Confidence badge */}
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${cb.text} ${cb.bg}`}>
                              <Shield size={10} />
                              {call.confidence_bucket}
                            </span>
                            {/* Consensus badge */}
                            {call.consensus && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase text-[#C5A059] bg-[#C5A059]/10"
                                title={`${call.consensus} independent AI models agreed on this call${call.avg_conviction ? ` at ${call.avg_conviction}% avg conviction` : ''}`}
                                data-testid="call-consensus-badge"
                              >
                                {call.consensus} AI
                              </span>
                            )}
                            {/* Technical verification badge */}
                            {call.tech_verification?.verified && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase text-emerald-400 bg-emerald-400/10"
                                title={`Verified by live technicals (${call.tech_verification.direction_score}/10 confluence): ${(call.tech_verification.supporting || []).join(', ')}`}
                                data-testid="call-ta-badge"
                              >
                                TA ✓
                              </span>
                            )}
                            {/* Time */}
                            <span className="text-[10px] text-gray-600 flex items-center gap-1">
                              <Clock size={10} />
                              {getTimeAgo(call.called_at)}
                            </span>
                          </div>
                        </div>

                        {/* Right: Price + Outcomes */}
                        <div className="flex items-center gap-3 sm:gap-4">
                          {/* Price at call */}
                          <div className="text-right">
                            <p className="text-[10px] text-gray-600 uppercase">Called at</p>
                            <p className="text-sm text-gray-300 font-mono">{formatPrice(call.price_at_call)}</p>
                          </div>

                          {/* Outcome columns */}
                          <div className="flex items-center gap-1.5">
                            <div className="text-center min-w-[50px]">
                              <p className="text-[9px] text-gray-600 uppercase mb-0.5">24h</p>
                              <OutcomeBadge outcome={call.outcome_24h} />
                              {call.pct_change_24h !== null && call.pct_change_24h !== undefined && (
                                <p className={`text-[10px] font-mono mt-0.5 ${call.pct_change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {call.pct_change_24h >= 0 ? '+' : ''}{call.pct_change_24h}%
                                </p>
                              )}
                            </div>
                            <div className="text-center min-w-[50px]">
                              <p className="text-[9px] text-gray-600 uppercase mb-0.5">48h</p>
                              <OutcomeBadge outcome={call.outcome_48h} />
                              {call.pct_change_48h !== null && call.pct_change_48h !== undefined && (
                                <p className={`text-[10px] font-mono mt-0.5 ${call.pct_change_48h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {call.pct_change_48h >= 0 ? '+' : ''}{call.pct_change_48h}%
                                </p>
                              )}
                            </div>
                            <div className="text-center min-w-[50px]">
                              <p className="text-[9px] text-gray-600 uppercase mb-0.5">7d</p>
                              <OutcomeBadge outcome={call.outcome_7d} />
                              {call.pct_change_7d !== null && call.pct_change_7d !== undefined && (
                                <p className={`text-[10px] font-mono mt-0.5 ${call.pct_change_7d >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {call.pct_change_7d >= 0 ? '+' : ''}{call.pct_change_7d}%
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Load More */}
                {calls.length < callsTotal && (
                  <div className="text-center pt-4">
                    <Button
                      onClick={loadMore}
                      disabled={loadingMore}
                      variant="outline"
                      className="border-white/20 text-gray-300 hover:text-white"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <><Loader2 size={14} className="animate-spin mr-2" /> Loading...</>
                      ) : (
                        <><ChevronDown size={14} className="mr-2" /> Load More ({callsTotal - calls.length} remaining)</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Disclaimer */}
            <div className="mt-12 pt-6 border-t border-white/5 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                <AlertTriangle size={14} className="text-yellow-500" />
                <p className="text-xs text-yellow-500/80">
                  Past performance does not indicate future results. This is not financial advice.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TrackRecord;
